import React from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { baseUrl } from '../../assets/Animations';
import { getRequestHeaders } from '../../api';
import { convertSchoolAbbreviationToFullName } from '../../Calendar/calendarFunctions';
import { buildInquiryLeadQuality } from './leadQualityUtils';
import './inquiry-lead-quality.css';

const RANGE_OPTIONS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'all', label: 'All collected data' },
];

const LEAD_DISTRIBUTION_COLORS = {
  Qualified: '#22c55e',
  'Too young': '#f59e0b',
  Pricing: '#ef4444',
  'Instrument not available': '#8b5cf6',
  'Wants in-studio': '#0ea5e9',
  'Only wants intro': '#ec4899',
  'Out-of-range': '#64748b',
};

const formatPercent = (value) => `${Math.round(Number(value) || 0)}%`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : format(date, 'MMM d, yyyy');
};

const LeadQualityMetric = ({ label, value, rate, tone, description }) => (
  <div className={`inquiry-lead-quality__metric is-${tone}`}>
    <div className="inquiry-lead-quality__metric-label">{label}</div>
    <div className="inquiry-lead-quality__metric-value-row">
      <strong>{value.toLocaleString()}</strong>
      {rate != null && <span>{formatPercent(rate)}</span>}
    </div>
    <div className="inquiry-lead-quality__metric-description">{description}</div>
  </div>
);

const InquiryLeadQuality = ({ families = [] }) => {
  const [logs, setLogs] = React.useState([]);
  const [selectedRange, setSelectedRange] = React.useState('90');
  const [selectedSchool, setSelectedSchool] = React.useState('ALL');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const loadStatusHistory = React.useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${baseUrl}/status-history-logs`, {
        ...getRequestHeaders(),
        params: { entityType: 'family' },
      });
      setLogs(Array.isArray(response?.data) ? response.data : []);
    } catch (requestError) {
      console.error('Failed to load inquiry lead quality:', requestError);
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          'Unable to load inquiry lead quality.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadStatusHistory();
  }, [loadStatusHistory]);

  const schools = React.useMemo(() => {
    const uniqueSchools = new Set();
    (Array.isArray(families) ? families : []).forEach((family) => {
      const school = String(family?.school || family?.School || '').trim();
      if (school) uniqueSchools.add(school);
    });
    return Array.from(uniqueSchools).sort((a, b) => a.localeCompare(b));
  }, [families]);

  const summary = React.useMemo(
    () =>
      buildInquiryLeadQuality({
        families,
        logs,
        range: selectedRange,
        school: selectedSchool,
      }),
    [families, logs, selectedRange, selectedSchool]
  );

  const selectedRangeLabel =
    RANGE_OPTIONS.find((option) => option.value === selectedRange)?.label || 'Selected period';
  const pieBackground = React.useMemo(() => {
    if (!summary.total) return '#e2e8f0';
    let cursor = 0;
    const segments = summary.leadDistribution
      .filter((item) => item.count > 0)
      .map((item) => {
        const start = cursor;
        cursor += item.percent;
        return `${LEAD_DISTRIBUTION_COLORS[item.category]} ${start}% ${cursor}%`;
      });
    return `conic-gradient(${segments.join(', ')})`;
  }, [summary.leadDistribution, summary.total]);

  if (loading) {
    return <div className="inquiry-lead-quality__loading">Loading lead-quality history…</div>;
  }

  if (error) {
    return (
      <div className="inquiry-lead-quality__error" role="alert">
        <span>{error}</span>
        <button type="button" onClick={loadStatusHistory}>Retry</button>
      </div>
    );
  }

  return (
    <div className="inquiry-lead-quality">
      <div className="inquiry-lead-quality__toolbar">
        <div className="inquiry-lead-quality__definition">
          One family counts as one inquiry. Unqualified leads are limited to: Too young, Pricing,
          Instrument not available, Wants in-studio, Only wants intro, and Out-of-range. Every
          other lead is qualified.
          {summary.trackingStartDate && (
            <span className="inquiry-lead-quality__coverage">
              History coverage begins {formatDate(summary.trackingStartDate)}.
            </span>
          )}
        </div>
        <div className="inquiry-lead-quality__filters">
          <label>
            <span>Inquiry period</span>
            <select value={selectedRange} onChange={(event) => setSelectedRange(event.target.value)}>
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>School</span>
            <select value={selectedSchool} onChange={(event) => setSelectedSchool(event.target.value)}>
              <option value="ALL">All schools</option>
              {schools.map((school) => (
                <option key={school} value={school}>
                  {convertSchoolAbbreviationToFullName(school)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="inquiry-lead-quality__metrics">
        <LeadQualityMetric
          label="Total inquiries"
          value={summary.total}
          tone="neutral"
          description={`${selectedRangeLabel}${selectedSchool === 'ALL' ? ' · all schools' : ` · ${selectedSchool}`}`}
        />
        <LeadQualityMetric
          label="Qualified leads"
          value={summary.qualifiedCount}
          rate={summary.qualifiedRate}
          tone="good"
          description="Eligible for qualified conversion reporting"
        />
        <LeadQualityMetric
          label="Unqualified leads"
          value={summary.unqualifiedCount}
          rate={summary.unqualifiedRate}
          tone="issue"
          description="One of the six excluded dispositions"
        />
      </div>

      {summary.total > 0 && (
        <div className="inquiry-lead-quality__quality-bar" aria-label={`${formatPercent(summary.qualifiedRate)} qualified leads and ${formatPercent(summary.unqualifiedRate)} unqualified leads`}>
          <div
            className="inquiry-lead-quality__quality-bar-good"
            style={{ width: `${summary.qualifiedRate}%` }}
          />
          <div
            className="inquiry-lead-quality__quality-bar-issue"
            style={{ width: `${summary.unqualifiedRate}%` }}
          />
        </div>
      )}

      {summary.total === 0 ? (
        <div className="inquiry-lead-quality__empty">
          No inquiries with a usable inquiry date were found for these filters.
        </div>
      ) : (
        <>
          <section
            className="inquiry-lead-quality__distribution"
            aria-labelledby="lead-distribution-title"
          >
            <div className="inquiry-lead-quality__section-header">
              <div>
                <h4 id="lead-distribution-title">Lead distribution</h4>
                <p>
                  Qualified leads and each unqualified disposition for the selected inquiry cohort.
                </p>
              </div>
            </div>
            <div className="inquiry-lead-quality__distribution-body">
              <div
                className="inquiry-lead-quality__pie"
                style={{ background: pieBackground }}
                role="img"
                aria-label={summary.leadDistribution
                  .map((item) => `${item.category}: ${item.count}`)
                  .join(', ')}
              />
              <div className="inquiry-lead-quality__pie-legend">
                {summary.leadDistribution.map((item) => (
                  <div key={item.category} className="inquiry-lead-quality__pie-legend-row">
                    <span
                      className="inquiry-lead-quality__pie-swatch"
                      style={{ background: LEAD_DISTRIBUTION_COLORS[item.category] }}
                    />
                    <span className="inquiry-lead-quality__pie-label">{item.category}</span>
                    <strong>{item.count}</strong>
                    <span>{formatPercent(item.percent)}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div className="inquiry-lead-quality__detail-grid">
            <section
              className="inquiry-lead-quality__breakdown"
              aria-labelledby="lead-issue-breakdown-title"
            >
              <div className="inquiry-lead-quality__section-header">
                <div>
                  <h4 id="lead-issue-breakdown-title">Recorded reason breakdown</h4>
                  <p>
                    Share of the {summary.issueCount} inquir
                    {summary.issueCount === 1 ? 'y' : 'ies'} with a recorded reason.
                  </p>
                </div>
              </div>
              {summary.reasonBreakdown.length ? (
                <div className="inquiry-lead-quality__reason-list">
                  {summary.reasonBreakdown.map((item) => (
                    <div key={item.reason} className="inquiry-lead-quality__reason-row">
                      <div className="inquiry-lead-quality__reason-heading">
                        <span>{item.reason}</span>
                        <strong>{item.count}</strong>
                      </div>
                      <div className="inquiry-lead-quality__reason-track">
                        <div style={{ width: `${item.percentOfIssues}%` }} />
                      </div>
                      <div className="inquiry-lead-quality__reason-percent">
                        {formatPercent(item.percentOfIssues)} of issues
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="inquiry-lead-quality__empty is-compact">
                  No reasons recorded.
                </div>
              )}
            </section>

            <section
              className="inquiry-lead-quality__recent"
              aria-labelledby="recent-lead-issues-title"
            >
              <div className="inquiry-lead-quality__section-header">
                <div>
                  <h4 id="recent-lead-issues-title">Recent recorded reasons</h4>
                  <p>The latest recorded reason for each affected inquiry.</p>
                </div>
              </div>
              {summary.issueRows.length ? (
                <div className="inquiry-lead-quality__table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Family</th>
                        <th>School</th>
                        <th>Reason</th>
                        <th>Recorded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.issueRows.slice(0, 10).map((row) => (
                        <tr key={row.familyId}>
                          <td>{row.familyName}</td>
                          <td>{row.school}</td>
                          <td>
                            <span className="inquiry-lead-quality__reason-pill">{row.reason}</span>
                          </td>
                          <td>{formatDate(row.issueDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {summary.issueRows.length > 10 && (
                    <div className="inquiry-lead-quality__table-note">
                      Showing the 10 most recent of {summary.issueRows.length} affected inquiries.
                    </div>
                  )}
                </div>
              ) : (
                <div className="inquiry-lead-quality__empty is-compact">
                  No reasons recorded.
                </div>
              )}
            </section>
          </div>
        </>
      )}

      {summary.excludedMissingDate > 0 && (
        <div className="inquiry-lead-quality__footnote">
          {summary.excludedMissingDate} famil{summary.excludedMissingDate === 1 ? 'y was' : 'ies were'} excluded because no inquiry date was available.
        </div>
      )}
    </div>
  );
};

export default InquiryLeadQuality;
